/**
 * Simple cookie utility for tracking returning visitors
 * Sets a cookie when user visits gated content or attempts signup
 */

/**
 * Set a cookie
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Days until expiration (default: 365)
 */
export function setCookie(name, value, days = 365) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

/**
 * Get a cookie value
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export function getCookie(name) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

/**
 * Check if user has visited before (has the returning visitor cookie)
 * @returns {boolean}
 */
export function isReturningVisitor() {
  return getCookie('vd_returning_visitor') === 'true';
}

/**
 * Mark user as returning visitor
 */
export function markAsReturningVisitor() {
  setCookie('vd_returning_visitor', 'true', 365);
}

// ============================================================================
// ORIGINAL REFERRER & UTM TRACKING
// ============================================================================
// Captures the original traffic source before OAuth redirects overwrite it.
// This solves the accounts.google.com referrer problem.

const STORAGE_KEY = 'vd_original_traffic';
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Capture and store the original referrer and UTM parameters on first page load.
 * Should be called once at app initialization.
 * Only stores if no existing session data or session has expired.
 */
export function captureOriginalTrafficSource() {
  if (typeof window === 'undefined') return;

  // Check if we already have valid session data
  const existing = getOriginalTrafficSource();
  if (existing && !existing.expired) {
    // Refresh the timestamp to extend the session
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
    data.timestamp = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return;
  }

  // Get the current referrer (only meaningful on first page load)
  const referrer = document.referrer || '';
  
  // Parse UTM parameters from URL
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {
    utm_source: urlParams.get('utm_source') || null,
    utm_medium: urlParams.get('utm_medium') || null,
    utm_campaign: urlParams.get('utm_campaign') || null,
    utm_term: urlParams.get('utm_term') || null,
    utm_content: urlParams.get('utm_content') || null,
  };

  // Don't overwrite if referrer is from Google OAuth (user is returning from login)
  const isOAuthRedirect = referrer.includes('accounts.google.com') || 
                          referrer.includes('google.com/accounts');
  
  if (isOAuthRedirect) {
    return; // Don't capture OAuth redirect as referrer
  }

  // Extract referrer domain for easier analysis
  let referrerDomain = null;
  if (referrer) {
    try {
      referrerDomain = new URL(referrer).hostname;
    } catch (e) {
      referrerDomain = referrer;
    }
  }

  // Store the traffic source data
  const trafficData = {
    referrer: referrer,
    referrer_domain: referrerDomain,
    utm: utmParams,
    landing_page: window.location.pathname + window.location.search,
    timestamp: Date.now(),
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(trafficData));
}

/**
 * Get the original traffic source data.
 * @returns {Object|null} Traffic source data or null if none/expired
 */
export function getOriginalTrafficSource() {
  if (typeof window === 'undefined') return null;

  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;

    const parsed = JSON.parse(data);
    const age = Date.now() - parsed.timestamp;
    
    // Check if session has expired
    if (age > SESSION_DURATION_MS) {
      return { ...parsed, expired: true };
    }

    return parsed;
  } catch (e) {
    return null;
  }
}

/**
 * Get the original referrer (before any OAuth redirects).
 * Falls back to document.referrer if no stored data.
 * @returns {string} The original referrer URL
 */
export function getOriginalReferrer() {
  const trafficSource = getOriginalTrafficSource();
  if (trafficSource && !trafficSource.expired) {
    return trafficSource.referrer || '';
  }
  return document.referrer || '';
}

/**
 * Get the original referrer domain.
 * @returns {string|null} The referrer domain or null
 */
export function getOriginalReferrerDomain() {
  const trafficSource = getOriginalTrafficSource();
  if (trafficSource && !trafficSource.expired) {
    return trafficSource.referrer_domain;
  }
  
  // Fallback to current document.referrer
  const referrer = document.referrer;
  if (referrer) {
    try {
      return new URL(referrer).hostname;
    } catch (e) {
      return null;
    }
  }
  return null;
}

/**
 * Get the original UTM parameters.
 * @returns {Object} UTM parameters object
 */
export function getOriginalUtmParams() {
  const trafficSource = getOriginalTrafficSource();
  if (trafficSource && !trafficSource.expired && trafficSource.utm) {
    return trafficSource.utm;
  }
  
  // Fallback to current URL params
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_term: urlParams.get('utm_term') || null,
      utm_content: urlParams.get('utm_content') || null,
    };
  }
  
  return {};
}

/**
 * Clear the stored traffic source (call after successful attribution).
 */
export function clearOriginalTrafficSource() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY);
  }
}